import React from "react"
import { Formik } from "formik"
import { Button } from "../../button/button"
import DropDown from "../../dropdowns"
import { Link } from "gatsby"

export default function DynamicForm({ form, onSubmit, className, autoFocus }) {
	console.log("form", form)
	if (!form || !form.contentfulfields) {
		console.error("Invalid form data:", form)
		return null
	}

	const initialValues = {
		...form.contentfulfields.reduce((acc, field) => {
			acc[field.name] = field.defaultValue || ""
			return acc
		}, {}),
		gdprConsent: false,
	}

	// Add validation schema
	const validate = (values) => {
		const errors = {}
		form.contentfulfields.forEach((field) => {
			// Check required fields
			if (field.required && !values[field.name]) {
				errors[field.name] = "This field is required"
			}

			// Email validation
			if (field.type.toLowerCase() === "email" && values[field.name]) {
				const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
				if (!emailRegex.test(values[field.name])) {
					errors[field.name] = "Invalid email address"
				}
			}
		})

		// GDPR consent validation
		if (!values.gdprConsent) {
			errors.gdprConsent = "You must accept the privacy policy to continue"
		}

		return errors
	}

	const renderField = (field, formikProps, isFirstField) => {
		const { values, handleChange, handleBlur, errors, touched } = formikProps

		const commonProps = {
			id: field.name,
			name: field.name,
			value: values[field.name],
			onChange: handleChange,
			onBlur: handleBlur,
			...(isFirstField && autoFocus ? { autoFocus: true } : {}),
			className: `block w-full shadow-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400 ${
				field.hidden ? "hidden" : ""
			} ${errors[field.name] && touched[field.name] ? "border-red-500" : ""}`,
		}

		// Add error message display
		const errorMessage =
			errors[field.name] && touched[field.name] ? (
				<div className="mt-1 text-sm text-red-600">{errors[field.name]}</div>
			) : null

		switch (field.type.toLowerCase()) {
			case "select":
				return (
					<div className={field.hidden ? "hidden" : ""}>
						<DropDown
							name={field.name}
							label={field.label}
							values={field.selectOptions || []}
							placeholder={field.placeholder}
						/>
					</div>
				)

			case "checkbox":
				return (
					<div className={field.hidden ? "hidden" : ""}>
						<label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
						<div className="space-y-2">
							{field.selectOptions?.map((option) => (
								<div
									key={option}
									className="relative flex items-start"
								>
									<div className="flex items-center h-5">
										<input
											type="checkbox"
											id={`${field.name}-${option}`}
											name={field.name}
											value={option}
											checked={
												Array.isArray(values[field.name]) && values[field.name].includes(option)
											}
											onChange={(e) => {
												const currentValues = Array.isArray(values[field.name])
													? values[field.name]
													: []
												if (e.target.checked) {
													handleChange({
														target: {
															name: field.name,
															value: [...currentValues, option],
														},
													})
												} else {
													handleChange({
														target: {
															name: field.name,
															value: currentValues.filter((val) => val !== option),
														},
													})
												}
											}}
											className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
										/>
									</div>
									<div className="ml-3 text-sm">
										<label
											htmlFor={`${field.name}-${option}`}
											className="font-medium text-gray-700"
										>
											{option}
										</label>
									</div>
								</div>
							))}
						</div>
					</div>
				)

			case "radio":
				return (
					<div className={field.hidden ? "hidden" : ""}>
						<label className="block text-sm font-medium text-gray-700">{field.label}</label>
						<div className="mt-2 space-y-2">
							{field.selectOptions?.map((option) => (
								<div
									key={option}
									className="flex items-center"
								>
									<input
										type="radio"
										id={`${field.name}-${option}`}
										name={field.name}
										value={option}
										checked={values[field.name] === option}
										onChange={handleChange}
										className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300"
									/>
									<label
										htmlFor={`${field.name}-${option}`}
										className="ml-3 text-sm text-gray-700"
									>
										{option}
									</label>
								</div>
							))}
						</div>
					</div>
				)

			case "textarea":
				return (
					<div className={field.hidden ? "hidden" : ""}>
						<label
							htmlFor={field.name}
							className="block text-sm font-medium text-gray-700"
						>
							{field.label}
							{field.required && <span className="text-red-500">*</span>}
						</label>
						<div className="mt-1">
							<textarea
								{...commonProps}
								rows={4}
								placeholder={field.placeholder}
								className={`${commonProps.className} placeholder-gray-400`}
							/>
						</div>
						{errorMessage}
					</div>
				)

			default:
				return (
					<div className={field.hidden ? "hidden" : ""}>
						<label
							htmlFor={field.name}
							className="block text-sm font-medium text-gray-700"
						>
							{field.label}
							{field.required && <span className="text-red-500">*</span>}
						</label>
						<div className="mt-1 relative rounded-md shadow-sm">
							<input
								type={field.type.toLowerCase()}
								placeholder={field.placeholder}
								{...commonProps}
							/>
						</div>
						{errorMessage}
					</div>
				)
		}
	}

	return (
		<Formik
			initialValues={initialValues}
			validate={validate}
			onSubmit={async (values, { setSubmitting }) => {
				try {
					// Create an AbortController to handle cleanup
					const abortController = new AbortController()
					const timeoutId = setTimeout(() => abortController.abort(), 10000) // 10s timeout

					await onSubmit(values)

					// Clear timeout after successful submission
					clearTimeout(timeoutId)
					setSubmitting(false)

					// Add a small delay before redirect to ensure cleanup
					await new Promise((resolve) => setTimeout(resolve, 100))
				} catch (error) {
					console.error("Form submission error:", error)
					setSubmitting(false)
				}
			}}
		>
			{(formikProps) => {
				const { handleSubmit, isSubmitting, values, handleChange, handleBlur } = formikProps
				return (
					<form
						onSubmit={handleSubmit}
						className={`w-full mt-4 ${className}`}
					>
						{form.contentfulfields.map((field, index) => (
							<div key={field.name}>{renderField(field, formikProps, index === 0)}</div>
						))}

						<div className="mt-4">
							<div className="relative flex items-start">
								<div className="flex items-center h-5">
									<input
										id="gdpr-consent"
										name="gdprConsent"
										type="checkbox"
										onChange={handleChange}
										onBlur={handleBlur}
										checked={values.gdprConsent || false}
										className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
									/>
								</div>
								<div className="ml-3 text-sm">
									<label
										htmlFor="gdpr-consent"
										className="text-gray-500"
									>
										I agree to receive marketing communications, updates, and resources from
										Fastvue. I understand that I can unsubscribe at any time. For more information,
										view Fastvue's{" "}
										<Link
											to="https://www.fastvue.co/legal/privacy-policy"
											target="_blank"
											className="text-primary-600 hover:text-primary-500 underline"
										>
											Privacy Policy
										</Link>
										.
									</label>
									{formikProps.errors.gdprConsent && formikProps.touched.gdprConsent && (
										<div className="mt-1 text-sm text-red-600">
											{formikProps.errors.gdprConsent}
										</div>
									)}
								</div>
							</div>
						</div>

						<div className="mt-6">
							<Button.BaseButton
								buttonStyle="fill"
								size="2xl"
								className="w-full"
								type="submit"
								disabled={isSubmitting}
							>
								{isSubmitting ? "Submitting..." : form.submitButtonText}
							</Button.BaseButton>
						</div>
					</form>
				)
			}}
		</Formik>
	)
}
